import React from 'react';
import PropTypes from 'prop-types';
import { getSortValueForFullName } from 'AppSrc/core/utility-functions/sort-functions';
import {
  getTimingTextForValue,
  getNetworkTextForValue,
  getDateDisplay,
  getRowItem,
  getCrqListRowId,
  getCrqListRowKey,
} from 'AppSrc/core/crq/crq-table-utils';
import { PaginatedSortableListTable } from 'AppSrc/core/components/tables/paginated-sortable-list-table';
import { usernameSelector } from 'AppSrc/core/auth/auth-selectors';
import { useHistory } from 'react-router';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import {
  PEER_TAB_ID,
  CIRCUITCHECK_TAB_ID,
  APPROVAL_TAB_ID,
  INITIAL_COMMS_SENT,
  APPROVED,
  CLOUSER,
} from './coordinator-work-queue-constants';

const CRQS_PER_PAGE = 10;
const START_DATE_COLUMN = 'startDate';
const END_DATE_COLUMN = 'endDate';
const SCHEDULED_END_DATE = 'ScheduledEndDate';
const STAGE = 'status';
// const impact = 'impact';

// column names for the possible columns
const columnNames = {
  [START_DATE_COLUMN]: 'Start Date',
  [END_DATE_COLUMN]: 'End Date',
  [SCHEDULED_END_DATE]: 'Scheduled End Date',
  crqId: 'CRQ ID',
  network: 'Network',
  timing: 'SLA',
  peer: 'Peer Reviewer',
  requestorName: 'Requestor',
  coordinatorName: 'Coordinator',
  rejectedBy: 'Rejected by',
  approvalType: 'Approval type',
  analyst: 'Analyst',
  [STAGE]: 'Stage',
  clouserTime: 'Time To Closure',
  summary: 'Summary',
};

const columnsWithNullLast = [
  START_DATE_COLUMN,
];


// map of column Ids for tab
const extraColumnIdsByTab = {
  [CIRCUITCHECK_TAB_ID]: [
    'coordinatorName',
    'network',
    'summary',
  ],
  [PEER_TAB_ID]: [
    START_DATE_COLUMN,
    'crqId',
    'network',
    'timing',
    'peer',
    'summary',
  ],
  [APPROVAL_TAB_ID]: [
    START_DATE_COLUMN,
    'crqId',
    'network',
    'timing',
    'summary',
  ],
  [INITIAL_COMMS_SENT]: [
    START_DATE_COLUMN,
    'crqId',
    'network',
    'timing',
    STAGE,
    'summary',
  ],
  [APPROVED]: [
    [START_DATE_COLUMN],
    [END_DATE_COLUMN],
    'crqId',
    'network',
    'timing',
    // STAGE,
    'summary',
  ],
  [CLOUSER]: [
    [END_DATE_COLUMN],
    'crqId',
    'network',
    'clouserTime',
    'summary',
  ],
};

const noResultsMessages = {
  [PEER_TAB_ID]: 'No CRQs to Peer Review',
  [CIRCUITCHECK_TAB_ID]: 'No CRQs requiring Circuit Check',
  [APPROVAL_TAB_ID]: 'No CRQs to Approve',
  [CLOUSER]: 'No CRQs for Pending Closure',
  [INITIAL_COMMS_SENT]: 'No CRQs for Initial Comms',
  [APPROVED]: 'No CRQs Approved',
};

const getColumnIdsForTab = (tab) => {
  const extraColumns = extraColumnIdsByTab[tab];
  return ([
    ...extraColumns,
  ]);
};

const getColumnsForTab = (tab) => {
  const columnIds = getColumnIdsForTab(tab);
  return columnIds.map((id) => {
    const name = columnNames[id];
    const sortWithNullLast = columnsWithNullLast.includes(id) ? true : false;
    return ({
      id,
      name,
      sortable: true,
      sortWithNullLast,
    });
  });
};

// TODO redo this based on the data returned from the API
const getRowForCrqAndTab = ({
  tab,
  crq,
  username,
  index,
}) => {
  const {
    id: crqId,
    start_date: startDate,
    end_date: endDate,
    status,
    scheduled_end_date: ScheduledEndDate,
    network,
    timing,
    requester_name: requestorName, // Mapping the new requester Id ELS Defect 9876
    coordinator_name: coordinatorName,
    peer_reviewer_name: peer,
    approval_type: approvalType,
    clouser_timing: clouserTime,
    adjacent_remedy_ref: adjacent,
    impact,
    summary,
  } = crq;

  const defaultValues = {
    crqId,
    startDate,
    endDate,
    status,
    ScheduledEndDate,
    network,
    timing,
    requestorName,
    coordinatorName,
    peer,
    approvalType,
    clouserTime,
    impact,
    summary,
  };

  // formating date
  const startDateDisplay = getDateDisplay(startDate);
  const endDateDisplay = getDateDisplay(endDate);
  // Look up text values for id fields
  // Differented the network for Mobile UK and Mobile VINE'Mobile(F)'
  let networkText = getNetworkTextForValue(network);
  const hasAdjRef = crq.hasOwnProperty('adjacent_remedy_ref');
  if (networkText === 'Mobile UK' && (adjacent !== null) && (hasAdjRef === true)) {
    networkText = 'Mobile(F)';
  }

  if (networkText === 'Mobile UK' && (adjacent === null)) {
    networkText = 'Mobile UK';
  }
  const timingText = getTimingTextForValue(network, `${timing}`);
  // hide the requester name if it is the same as the logged in user
  const requestorNameDisplay = requestorName === username ? '' : requestorName;
  // Added new column 'Summary' in the My CRQ dashboard CRQ table to view CRQ summary on the dashboard.
  const isSummary = (
    <div className="column-summary dashboard-summary">
      <span className="row-item-text" data-tip={summary}>
        {summary}
      </span>
      <ReactTooltip />
    </div>
  );

  // Display values for each column
  // mostly direct from the crq data,
  // but some columns are formateed
  const displayValues = {
    ...defaultValues,
    startDate: startDateDisplay,
    endDate: endDateDisplay,
    network: networkText,
    timing: timingText,
    requestorName: requestorNameDisplay,
    summary: isSummary,
  };

  // sort values for the crq
  // mostly direct from the crq data
  // but some fields have specific sort values
  const sortValues = {
    ...defaultValues,
    network: networkText,
    timing: timingText,
    coordinatorName: getSortValueForFullName(coordinatorName),
    requestorName: getSortValueForFullName(requestorName),
    // summary: isSummary,
  };

  const columnIds = getColumnIdsForTab(tab);
  const rowId = getCrqListRowId(network, crqId);
  const rowKey = getCrqListRowKey(crqId, index);
  const row = {
    id: rowId,
    key: rowKey,
    cells: [],
    sortValues: {},
  };

  // go through the visible columns and add in the row cells and the sort data
  columnIds.forEach((columnId, colIndex) => {
    const sortValue = sortValues[columnId];
    const newText = 'hrs';
    const displayText = columnId === 'clouserTime' ? displayValues[columnId] + newText : displayValues[columnId];
    const lastColumn = colIndex === columnIds.length - 1;
    const cell = getRowItem({
      columnId,
      displayText,
      withChevron: lastColumn,
    });
    row.cells.push(cell);
    row.sortValues[columnId] = sortValue;
  });

  return row;
};


const MyCRQCoordinatorWorkQueueTableDisplay = ({
  tab,
  crqList,
  username,
}) => {
  const columns = getColumnsForTab(tab);
  const rows = crqList.map((crq, index) => getRowForCrqAndTab({
    tab,
    crq,
    username,
    index,
  }));
  const history = useHistory();
  const noResultsMessage = noResultsMessages[tab];
  const isApprovals = tab === APPROVAL_TAB_ID;
  const isDuplicate = true;
  return (
    <div className="coordinator-work-queue-table">
      <PaginatedSortableListTable
        key={tab} // guarantee re-render when changing tabs
        rows={rows}
        columns={columns}
        rowsPerPage={CRQS_PER_PAGE}
        onClickRow={(rowId) => { // rowId is E.G 'mobile/CRQ-000001'
          // for approvals tab, add `/approvals` to the url
          const path = `/crq/${rowId}${isApprovals ? '/approvals' : ''}`;
          history.push(path);
          window.localStorage.removeItem('isInfoItem');
        }}
        className={`homepage-crq-table homepage-crq-table-${tab}`}
        initialSortedColumn="startDate"
        initialSortDesc={false}
        noResultsMessage={noResultsMessage}
        isDuplicate={isDuplicate}
      />
    </div>
  );
};

MyCRQCoordinatorWorkQueueTableDisplay.propTypes = {
  tab: PropTypes.string,
  crqList: PropTypes.arrayOf(PropTypes.object),
  username: PropTypes.string,
};

const mapStateToProps = (state, ownProps) => ({
  // for now retrieve the username from the auth login
  // to compare to requester_name
  // TODO: change this to compare requester_id with logged in user id
  // once VTO-516 is complete
  username: usernameSelector(state),
});

const MYCoordinatorWorkQueueTable = connect(
  mapStateToProps,
)(MyCRQCoordinatorWorkQueueTableDisplay);

export {
  MYCoordinatorWorkQueueTable,
  MyCRQCoordinatorWorkQueueTableDisplay,
};
